import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useAdminSupportTickets, useAdminSupportTicket, useAdminReplyTicket, useAdminCloseTicket } from '../api/useAdmin'
import { mediaUrl } from '../utils/media'
import { TICKET_STATUS_LABEL as STATUS_LABEL, TICKET_STATUS_CSS as STATUS_CSS } from '../utils/constants'
import { formatDateTime as formatDate } from '../utils/date'
import Pagination from '../components/ui/Pagination'
import toast from 'react-hot-toast'

export function AdminSupportList() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const { data, isLoading } = useAdminSupportTickets(page, statusFilter)
  const tickets = data?.items || []
  const pages = data?.pages || 1
  const total = data?.total || 0

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-sm text-muted hover:text-accent transition-colors">← Админ</Link>
        <span className="text-line">/</span>
        <h1 className="text-xl font-bold">Поддержка</h1>
        {total > 0 && <span className="text-sm text-muted">{total}</span>}
      </div>

      <div className="flex gap-2 mb-5">
        {['', 'open', 'answered', 'closed'].map((s) => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1) }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === s ? 'bg-accent text-on-accent' : 'bg-surface text-muted hover:text-fg'}`}>
            {s === '' ? 'Все' : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-xl animate-pulse bg-surface" />)}
        </div>
      ) : tickets.length === 0 ? (
        <p className="text-center py-16 text-muted">Обращений нет</p>
      ) : (
        <>
          <div className="rounded-2xl overflow-hidden border border-line">
            {tickets.map((t, i) => (
              <Link
                key={t.id}
                to={`/admin/support/${t.id}`}
                className={`flex items-center gap-4 px-4 py-3 bg-surface hover:bg-surface2 transition-colors ${i < tickets.length - 1 ? 'border-b border-line' : ''}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{t.subject}</p>
                  <p className="text-xs text-muted">{t.user?.username || 'Удалённый'} · {formatDate(t.created_at)}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 font-medium ${STATUS_CSS[t.status] || STATUS_CSS.closed}`}>
                  {STATUS_LABEL[t.status] || t.status}
                </span>
              </Link>
            ))}
          </div>
          <Pagination page={page} total={pages} onChange={setPage} />
        </>
      )}
    </div>
  )
}

export function AdminSupportTicketView() {
  const { id } = useParams()
  const { data: ticket, isLoading } = useAdminSupportTicket(id)
  const [replyText, setReplyText] = useState('')
  const { mutate: reply, isPending: replying } = useAdminReplyTicket(id)
  const { mutate: close, isPending: closing } = useAdminCloseTicket(id)

  const handleReply = (e) => {
    e.preventDefault()
    if (!replyText.trim()) return
    reply(replyText.trim(), {
      onSuccess: () => { toast.success('Ответ отправлен'); setReplyText('') },
      onError: () => toast.error('Ошибка'),
    })
  }

  if (isLoading) return <div className="max-w-2xl mx-auto px-4 py-10"><div className="h-48 rounded-2xl animate-pulse bg-surface" /></div>
  if (!ticket) return null

  const isClosed = ticket.status === 'closed'

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/support" className="text-sm text-muted hover:text-accent transition-colors">← Поддержка</Link>
        <span className="text-line">/</span>
        <h1 className="text-lg font-bold truncate">{ticket.subject}</h1>
        <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 font-medium ${STATUS_CSS[ticket.status] || STATUS_CSS.closed}`}>
          {STATUS_LABEL[ticket.status] || ticket.status}
        </span>
        {!isClosed && (
          <button onClick={() => close(undefined, { onSuccess: () => toast.success('Обращение закрыто'), onError: () => toast.error('Ошибка') })} disabled={closing}
            className="ml-auto px-3 py-1.5 rounded-lg text-sm font-semibold text-muted border border-line hover:text-red-400 hover:border-red-400/40 transition-colors disabled:opacity-50">
            Закрыть
          </button>
        )}
      </div>

      {ticket.user && (
        <div className="mb-4 p-3 rounded-xl bg-surface border border-line flex items-center gap-3">
          <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-xs font-bold bg-surface2 text-accent">
            {ticket.user.avatar_url
              ? <img src={mediaUrl(ticket.user.avatar_url)} alt="" className="w-full h-full object-cover" />
              : ticket.user.username.slice(0, 2).toUpperCase()}
          </div>
          <span className="text-sm font-semibold">{ticket.user.username}</span>
          <Link to={`/admin/users`} className="ml-auto text-xs text-accent hover:underline">Профиль</Link>
        </div>
      )}

      <div className="flex flex-col gap-3 mb-6 max-h-[480px] overflow-y-auto pr-1">
        {(ticket.messages || []).map((msg) => (
          <div key={msg.id} className={`flex gap-3 p-4 rounded-xl ${msg.is_admin ? 'bg-accent-dim border border-accent/20' : 'bg-surface'}`}>
            <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-xs font-bold bg-surface2 text-accent">
              {msg.author?.avatar_url
                ? <img src={mediaUrl(msg.author.avatar_url)} alt="" className="w-full h-full object-cover" />
                : (msg.author?.username || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold">{msg.author?.username || (msg.is_admin ? 'Поддержка' : 'Пользователь')}</span>
                {msg.is_admin && <span className="text-xs px-1.5 py-0.5 rounded bg-accent text-on-accent">Админ</span>}
                <span className="text-xs text-muted ml-auto">{formatDate(msg.created_at)}</span>
              </div>
              <p className="text-sm text-fg leading-relaxed">{msg.text}</p>
            </div>
          </div>
        ))}
      </div>

      {!isClosed && (
        <form onSubmit={handleReply} className="flex flex-col gap-3">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Ответ пользователю..."
            rows={3}
            className="w-full px-3 py-2 rounded-xl text-sm outline-none bg-surface border border-line text-fg focus:border-accent transition-colors resize-none"
          />
          <div className="flex justify-end">
            <button type="submit" disabled={replying || !replyText.trim()}
              className="px-4 py-2 rounded-lg text-sm font-bold bg-accent text-on-accent disabled:opacity-50">
              Ответить
            </button>
          </div>
        </form>
      )}
      {isClosed && <p className="text-center text-sm text-muted py-4">Обращение закрыто</p>}
    </div>
  )
}
